import {
	Main,
	Authorization,
	Registration,
	UserProfile,
	CurrentBiddings,
	CompletedBiddings,
	Rules,
	PasswordRecovery,
	BiddingCard,
	FavoriteBiddings,
	Rule,
	UserFilters,
	Payment
} from './pages';
import {
	Layout,
	AcceptNotifications,
	Popup
} from './components';
import {
	createBrowserRouter,
	RouterProvider,
	redirect
} from "react-router-dom";
import { $showNotification, $notificationMessage, $severity, setShowNotification } from './store/notification';
import { useUnit } from 'effector-react';
import Cookies from 'universal-cookie';
import { setIsAuth } from './store/isAuthStore'
import { jwtDecode } from 'jwt-decode';
import { getChatHistory, getCurrentUser } from './components/api';
import { $userData, setUserData, setUserDataLoaded } from './store/userData';
import { setChatMessages, $chatSocketConnection, setChatSocketConnection, addChatMessage, editChatMessage, deleteChatMessage, setBlockChat } from './store';
import { Snackbar, Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import {
	checkNotificationSupport
} from './services/notificationService';
import { setPushNotification } from './store/pushNotifications';
import * as signalR from '@microsoft/signalr';
import Banner from './components/Banner';
import { isMobile } from 'react-device-detect';
import { ConfigProvider } from 'antd';
import locale from 'antd/locale/ru_RU';
import { getTheme as getAppTheme } from './styles/themes';
import { routes } from './components/routes';

const isTokenExpired = (token) => {
	if (!token) {
		return true; // Если токена нет, он "просрочен"
	}
	try {
		const decodedToken = jwtDecode(token);
		const currentTime = Date.now() / 1000;
		return decodedToken.exp < currentTime;
	} catch (error) {
		console.error("Error decoding token:", error);
		return true;
	}
};

const cookies = new Cookies();
const token = cookies.get('token');
const acceptNotificationsShowed = cookies.get('acceptNotificationsShowed');
let isAuth = !isTokenExpired(token);
setIsAuth(isAuth);

const webSocketUrl = process.env.REACT_APP_SOCKET_URL;

const router = createBrowserRouter([
	{
		loader: async () => {
			if (!isAuth)
				throw redirect(routes.authorization);
			return null;
		},
		children: [
			{ path: routes.userProfile, element: <Layout children={<UserProfile />} /> },
			{ path: routes.favoriteBiddings, element: <Layout children={<FavoriteBiddings />} /> },
			{ path: routes.userFiltes, element: <Layout children={<UserFilters />} /> }
		]
	},
	{
		loader: async () => {
			if (isAuth)
				throw redirect(routes.root);

			return null;
		},
		children: [
			{ path: routes.authorization, element: <Layout children={<Authorization />} /> },
			{ path: routes.registration, element: <Registration /> },
			{ path: routes.passwordRecovery, element: <PasswordRecovery /> }
		]
	},
	{
		path: routes.root,
		element: <Layout children={<Main />} />
	},
	{
		path: routes.currentBiddings,
		element: <Layout children={<CurrentBiddings />} />
	},
	{
		path: routes.completedBiddings,
		element: <Layout children={<CompletedBiddings />} />
	},
	{
		path: routes.payment,
		element: <Layout children={<Payment />} />
	},
	{
		path: routes.rules,
		element: <Layout children={<Rules />} />
	},
	{
		path: routes.biddingCard,
		element: <Layout children={<BiddingCard />} />
	},
	{
		path: routes.rule,
		element: <Layout children={<Rule />} />
	}
]);

function App() {

	const [isSupported, setIsSupported] = useState(false);
	const [isSubscribed, setIsSubscribed] = useState(false);
	const [showPopup, setShowPopup] = useState(false);
	const [notificationSocketConnection, setNotificationSocketConnection] = useState(null);

	const showNotification = useUnit($showNotification);
	const notificationMessage = useUnit($notificationMessage);
	const severity = useUnit($severity);
	const chatSocketConnection = useUnit($chatSocketConnection);
	const userData = useUnit($userData);

	useEffect(() => {
		setIsSupported(checkNotificationSupport());
		checkSubscription();
	}, []);

	const checkSubscription = async () => {
		if ('serviceWorker' in navigator) {
			const reg = await navigator.serviceWorker.ready;
			const subscription = await reg.pushManager.getSubscription();
			setIsSubscribed(!!subscription);
		}
	};

	useEffect(() => {
		if (!isSubscribed && isAuth) {
			const timer = setTimeout(() => {
				setShowPopup(!isSubscribed && isAuth && !acceptNotificationsShowed);
			}, 1000);

			return () => clearTimeout(timer);
		}
	}, [isSubscribed, isAuth])


	useEffect(() => {
		if (isAuth) {
			getCurrentUser().then(data => {
				setUserDataLoaded(true);
				setUserData(data);
				loadChatHistory();
			})
				.catch(error => {
					setIsAuth(false);
					if (error?.response?.status !== 401)
						console.log(error)
				})
		}
		else {
			setUserDataLoaded(true);
		}
	}, [isAuth]);

	const loadChatHistory = () => {
		getChatHistory().then(data => {
			setChatMessages(data);
		})
			.catch(error => {
				console.log(error)
			})
	}

	useEffect(() => {
		if (!chatSocketConnection && isAuth) {
			const cookies = new Cookies();
			const token = cookies.get('token');

			const newConnection = new signalR.HubConnectionBuilder()
				.withUrl(`${webSocketUrl}/sockets/chat`, {
					accessTokenFactory: () => token,
					skipNegotiation: true,
					transport: signalR.HttpTransportType.WebSockets
				})
				.withAutomaticReconnect()
				.build();

			setChatSocketConnection(newConnection);
		}
	}, [isAuth, chatSocketConnection]);

	useEffect(() => {
		if (chatSocketConnection && userData.id && chatSocketConnection.state === signalR.HubConnectionState.Disconnected) {
			chatSocketConnection.start().then(() => {
				chatSocketConnection.on("ReceiveMessage", (message) => {
					addChatMessage(message);
				});
			
				chatSocketConnection.on("EditMessage", (message) => {
					editChatMessage(message);
				});

				chatSocketConnection.on("DeleteMessage", (message) => {
					deleteChatMessage(message);
				});

				chatSocketConnection.on("BlockChat", (userIsBlocked) => {
					setBlockChat(userIsBlocked);
				});
			}).catch((exception) => console.log(exception));
		}
	}, [chatSocketConnection, userData]);

	useEffect(() => {
		if (!notificationSocketConnection) {
			const newConnection = new signalR.HubConnectionBuilder()
				.withUrl(`${webSocketUrl}/sockets/notifications`, {
					skipNegotiation: true,
					transport: signalR.HttpTransportType.WebSockets
				})
				.withAutomaticReconnect()
				.build();

			setNotificationSocketConnection(newConnection);
		}
	}, []);

	useEffect(() => {
		if (notificationSocketConnection && userData.id && notificationSocketConnection.state === signalR.HubConnectionState.Disconnected) {
			notificationSocketConnection.start().then(() => {
				notificationSocketConnection.invoke("SendNotifications", userData.id);
				notificationSocketConnection.on("NotificationsListUpdate", (data) => {
					setPushNotification(JSON.parse(data));
				});
			}).catch((exception) => console.log(exception));
		}
	}, [notificationSocketConnection, userData]);

	const handleCloseAcceptNotifications = () => {
		cookies.set('acceptNotificationsShowed', true, { path: '/' });
		setShowPopup(false);
	};

	return (
		<ConfigProvider theme={getAppTheme()} locale={locale}>
			<div vertical="true">
				{isMobile && (<Banner />)}

				<RouterProvider router={router} />

				<Popup show={showPopup} onClose={() => handleCloseAcceptNotifications()}>
					<AcceptNotifications setShowPopup={() => handleCloseAcceptNotifications()} setIsSubscribed={(value) => setIsSubscribed(value)} isSupported={isSupported} />
				</Popup>
				<Snackbar open={showNotification} autoHideDuration={5000} onClose={() => setShowNotification(false)} style={{ position: 'fixed', bottom: '100px', zIndex: "10000 !important" }}>
					<Alert onClick={() => setShowNotification(false)} severity={severity}>
						{notificationMessage}
					</Alert>
				</Snackbar>
			</div>
		</ConfigProvider>
	);
}

export default App;
